import { ActionContext, Module } from 'vuex';

import {
  getStaffs,
  createGroup,
  addStaff,
  getGroups,
  getGroupById,
  deleteGroup,
  editGroupName,
  getGroupPerms,
  addGroupPerms,
  removeGroupPerms,
  getSubModulePerms,
  getSubModules,
  staffStatusUpdate,
  editUser,
  addUserToGroup,
  removeUserFromGroup,
  resendInviteLink,
  getBranches,
  createBranch,
  updateBranch,
  deactivateBranch,
  reactivateBranch,
  shutdownBranch,
  addModulesToBranch,
  removeMemberFromBranch
} from "@/api.resource/api/identity";

import {
  IRootState,
  IIdentityState,
  IPractitioner,
  StaffStatusQueryKey,
  IGroup,
  ICreateBranchDetailsPayload,
  IUpdateBranchDetailsPayload,
  IUpdateBranchStatusPayload
} from "@/types";

const identity: Module<IIdentityState, IRootState> = {
  namespaced: true,
  state: (): IIdentityState => ({
    loading: false,
    isLoadingMore: false,
    staffsData: [],
    staffsDataSuccess: false,
    staffsDataMsg: null,
    staffsTotalCount: 0,
    staffsResponse: null,
    createUser: false,
    saveUserInfo: null,
    saveUserSuccess: false,
    saving: false,
    createGroupSuccess: false,
    createGroupMsg: null,
    groupsLoading: false,
    groupsData: [],
    getGroupsSuccess: false,
    getGroupsMsg: null,
    groupLoading: false,
    groupDetails: null,
    groupDetailsSuccess: false,
    groupDetailsMsg: null,
    deleteGroupSuccess: false,
    deleteGroupMsg: null,
    editGroupNameSuccess: false,
    editGroupNameMsg: null,
    permsLoading: false,
    groupPermsData: null,
    groupPermsSuccess: false,
    groupPermsMsg: null,
    addGroupPermsSuccess: false,
    addGroupPermsMsg: null,
    removeGroupPermsSuccess: false,
    removeGroupPermsMsg: null,
    getSubModulesPermsLoading: false,
    getSubModulesPermsData: null,
    getSubModulesPermsSuccess: false,
    subModulesLoading: false,
    subModulesData: [],
    subModulesSuccess: false,
    subModulesMsg: null,
    staffStatusUpdateLoading: false,
    staffStatusUpdateSuccess: false,
    staffStatusUpdateMsg: null,
    editUserSuccess: false,
    editUserMsg: null,
    editUser: false,
    addUserToGroupSuccess: false,
    addUserToGroupMsg: null,
    removeUserFromGroupLoading: false,
    removeUserFromGroupSuccess: false,
    removeUserFromGroupMsg: null,
    staffStatusValue: null,
    resendInviteLinkSuccess: false,
    resendInviteLinkMsg: null,
    resendInvite: false,
    branchListLoading: false,
    branchList: [],
    branchListSuccess: false,
    branchListMsg: '',
    branchTotalCount: 0,
    createBranchLoading: false,
    createBranchSuccess: false,
    createBranchMsg: null,
    createBranch: null,
    updateBranchLoading: false,
    updateBranchSuccess: false,
    updateBranchMsg: null,
    branchStatusLoading: false,
    branchStatusSuccess: false,
    branchStatusMsg: null,
    removeMemberLoading: false,
    removeMemberSuccess: false,
    removeMemberMsg: null,
    addModulesLoading: false,
    addModulesSuccess: false,
    addModulesMsg: null,
  }),
  getters: {
    loading: state => state.loading,
    isLoadingMore: state => state.isLoadingMore,
    staffsData: state => state.staffsData,
    staffsDataSuccess: state => state.staffsDataSuccess,
    staffsDataMsg: state => state.staffsDataMsg,
    staffsTotalCount: state => state.staffsTotalCount,
    staffsResponse: state => state.staffsResponse,
    createUser: state => state.createUser,
    saveUserInfo: state => state.saveUserInfo,
    saveUserSuccess: state => state.saveUserSuccess,
    saving: state => state.saving,
    createGroupSuccess: state => state.createGroupSuccess,
    createGroupMsg: state => state.createGroupMsg,
    groupsLoading: state => state.groupsLoading,
    groupsData: state => state.groupsData,
    groupLoading: state => state.groupLoading,
    groupDetails: state => state.groupDetails,
    groupDetailsSuccess: state => state.groupDetailsSuccess,
    groupDetailsMsg: state => state.groupDetailsMsg,
    getGroupsSuccess: state => state.getGroupsSuccess,
    getGroupsMsg: state => state.getGroupsMsg,
    deleteGroupSuccess: state => state.deleteGroupSuccess,
    deleteGroupMsg: state => state.deleteGroupMsg,
    editGroupNameSuccess: state => state.editGroupNameSuccess,
    editGroupNameMsg: state => state.editGroupNameMsg,
    permsLoading: state => state.permsLoading,
    groupPermsData: state => state.groupPermsData,
    groupPermsSuccess: state => state.groupPermsSuccess,
    groupPermsMsg: state => state.groupPermsMsg,
    addGroupPermsSuccess: state => state.addGroupPermsSuccess,
    addGroupPermsMsg: state => state.addGroupPermsMsg,
    removeGroupPermsSuccess: state => state.removeGroupPermsSuccess,
    removeGroupPermsMsg: state => state.removeGroupPermsMsg,
    getSubModulesPermsLoading: state => state.getSubModulesPermsLoading,
    getSubModulesPermsData: state => state.getSubModulesPermsData,
    getSubModulesPermsSuccess: state => state.getSubModulesPermsSuccess,
    subModulesLoading: state => state.subModulesLoading,
    subModulesData: state => state.subModulesData,
    subModulesSuccess: state => state.subModulesSuccess,
    subModulesMsg: state => state.subModulesMsg,
    staffStatusUpdateLoading: state => state.staffStatusUpdateLoading,
    staffStatusUpdateSuccess: state => state.staffStatusUpdateSuccess,
    staffStatusUpdateMsg: state => state.staffStatusUpdateMsg,
    editUserSuccess: state => state.editUserSuccess,
    editUserMsg: state => state.editUserMsg,
    editUser: state => state.editUser,
    addUserToGroupSuccess: state => state.addUserToGroupSuccess,
    addUserToGroupMsg: state => state.addUserToGroupMsg,
    removeUserFromGroupLoading: state => state.removeUserFromGroupLoading,
    removeUserFromGroupSuccess: state => state.removeUserFromGroupSuccess,
    removeUserFromGroupMsg: state => state.removeUserFromGroupMsg,
    staffStatusValue: state => state.staffStatusValue,
    resendInviteLinkSuccess: state => state.resendInviteLinkSuccess,
    resendInviteLinkMsg: state => state.resendInviteLinkMsg,
    resendInvite: state => state.resendInvite,
    branchListLoading: state => state.branchListLoading,
    branchList: state => state.branchList,
    branchListSuccess: state => state.branchListSuccess,
    branchListMsg: state => state.branchListMsg,
    branchTotalCount: state => state.branchTotalCount,
    updateBranchLoading: state => state.updateBranchLoading,
    updateBranchSuccess: state => state.updateBranchSuccess,
    updateBranchMsg: state => state.updateBranchMsg,
    branchStatusLoading: state => state.branchStatusLoading,
    branchStatusSuccess: state => state.branchStatusSuccess,
    branchStatusMsg: state => state.branchStatusMsg,
    removeMemberLoading: state => state.removeMemberLoading,
    removeMemberSuccess: state => state.removeMemberSuccess,
    removeMemberMsg: state => state.removeMemberMsg,
    createBranchLoading: state => state.createBranchLoading,
    createBranchSuccess: state => state.createBranchSuccess,
    createBranchMsg: state => state.createBranchMsg,
    createBranch: state => state.createBranch,
    addModulesLoading: state => state.addModulesLoading,
    addModulesSuccess: state => state.addModulesSuccess,
    addModulesMsg: state => state.addModulesMsg,
  },
  mutations: {
    LOADING(state) {
      state.loading = true;
    },
    SET_LOADING_MORE(state, payload: boolean) {
      state.isLoadingMore = payload;
    },
    LOADING_STOP(state) {
      state.loading = false;
    },
    SAVING(state) {
      state.saving = true;
    },
    SAVING_STOP(state) {
      state.saving = false;
    },
    GROUPS_LOADING(state) {
      state.groupsLoading = true;
    },
    GROUPS_LOADING_STOP(state) {
      state.groupsLoading = false;
    },
    PERMS_LOADING(state) {
      state.permsLoading = true;
    },
    PERMS_LOADING_STOP(state) {
      state.permsLoading = false;
    },
    SUBMODULES_PERMS_LOADING(state) {
      state.getSubModulesPermsLoading = true;
    },
    SUBMODULES_PERMS_LOADING_STOP(state) {
      state.getSubModulesPermsLoading = false;
    },
    SUBMODULES_LOADING(state) {
      state.subModulesLoading = true;
    },
    SUBMODULES_LOADING_STOP(state) {
      state.subModulesLoading = false;
    },
    STAFF_STATUS_LOADING(state) {
      state.staffStatusUpdateLoading = true;
    },
    STAFF_STATUS_LOADING_STOP(state) {
      state.staffStatusUpdateLoading = false;
    },
    CREATE_USER(state) {
      state.createUser = true;
    },
    CREATE_USER_STOP(state) {
      state.createUser = false;
    },
    RESEND_INVITE(state) {
      state.resendInvite = true;
    },
    RESEND_INVITE_STOP(state) {
      state.resendInvite = false;
    },
    SAVE_STAFFS_DATA(state, payload: IPractitioner[]) {
      state.staffsData = payload;
    },
    APPEND_STAFFS_DATA(state, payload: IPractitioner[]) {
      const existingIds = new Set(state.staffsData.map(staff => staff.id));
      const newStaffs = payload.filter(staff => !existingIds.has(staff.id));
      state.staffsData = [...state.staffsData, ...newStaffs];
    },
    GET_STAFFS_SUCCESS(state, payload) {
      state.staffsDataSuccess = true;
      state.staffsDataMsg = payload?.data?.Description;
      state.staffsTotalCount = payload?.data?.totalRecords;
      state.staffsResponse = payload;
    },
    GET_STAFFS_ERROR(state, payload) {
      state.staffsDataSuccess = false;
      state.staffsDataMsg = payload;
      state.staffsData = [];
      state.staffsTotalCount = 0;
      state.staffsResponse = null;
    },
    RESET_STAFFS_DATA(state) {
      state.loading = false;
      state.isLoadingMore = false;
      state.staffsDataSuccess = false;
      state.staffsData = [];
      state.staffsResponse = null;
      state.staffsTotalCount = 0;
    },
    SAVE_USER_SUCCESS(state, payload) {
      state.createUser = false;
      state.saveUserSuccess = true;
      state.saveUserInfo = payload;
    },
    SAVE_USER_ERROR(state, payload) {
      state.createUser = false;
      state.saveUserSuccess = false;
      state.saveUserInfo = payload;
    },
    RESET_SAVE_USER(state) {
      state.createUser = false;
      state.saveUserSuccess = false;
      state.saveUserInfo = null;
    },
    CREATE_GROUP_SUCCESS(state, payload) {
      state.saving = false;
      state.createGroupSuccess = true;
      state.createGroupMsg = payload?.Description;
    },
    CREATE_GROUP_ERROR(state, payload) {
      state.saving = false;
      state.createGroupSuccess = false;
      state.createGroupMsg = payload;
    },
    RESET_CREATE_GROUP(state, payload) {
      state.saving = false;
      state.createGroupSuccess = false;
      state.createGroupMsg = null;
    },
    GET_GROUPS_SUCCESS(state, payload) {
      state.groupsLoading = false;
      state.getGroupsSuccess = true;
      state.groupsData = payload?.data;
      state.getGroupsMsg = payload?.Description;
    },
    GET_GROUPS_ERROR(state, payload) {
      state.groupsLoading = false;
      state.getGroupsSuccess = false;
      state.getGroupsMsg = payload;
      state.groupsData = [];
    },
    RESET_GET_GROUPS(state) {
      state.saving = false;
      state.groupsLoading = false;
      state.getGroupsSuccess = false;
      state.groupsData = [];
      state.getGroupsMsg = null;
    },
    SET_GROUP_LOADING(state, payload: boolean) {
      state.groupLoading = payload;
    },
    SET_GROUP_DETAILS(state, payload: IGroup) {
      state.groupDetails = payload;
      state.groupDetailsSuccess = true;
      state.groupDetailsMsg = null;
    },
    SET_GROUP_DETAILS_ERROR(state, payload: string | null) {
      state.groupDetails = null;
      state.groupDetailsSuccess = false;
      state.groupDetailsMsg = payload;
    },
    RESET_GET_GROUP(state) {
      state.groupLoading = false;
      state.groupDetails = null;
      state.groupDetailsSuccess = false;
      state.groupDetailsMsg = null;
    },
    DELETE_GROUP_SUCCESS(state, payload) {
      state.saving = false;
      state.deleteGroupSuccess = true;
      state.deleteGroupMsg = payload;
    },
    DELETE_GROUP_ERROR(state, payload) {
      state.saving = false;
      state.deleteGroupSuccess = false;
      state.deleteGroupMsg = payload;
    },
    RESET_DELETE_GROUP(state) {
      state.saving = false;
      state.deleteGroupSuccess = false;
      state.deleteGroupMsg = null;
    },
    RESET_EDIT_GROUP_NAME(state) {
      state.saving = false;
      state.editGroupNameMsg = null;
      state.editGroupNameSuccess = false;
    },
    EDIT_GROUP_NAME_SUCCESS(state, payload) {
      state.editGroupNameMsg = payload;
      state.editGroupNameSuccess = true;
    },
    EDIT_GROUP_NAME_ERROR(state, payload) {
      state.editGroupNameMsg = payload;
      state.editGroupNameSuccess = false;
    },
    GET_GROUP_PERMS_SUCCESS(state, payload) {
      state.permsLoading = false;
      state.groupPermsSuccess = true;
      state.groupPermsData = payload?.data;
      state.groupPermsMsg = payload?.Description;
    },
    GET_GROUP_PERMS_ERROR(state, payload) {
      state.permsLoading = false;
      state.groupPermsSuccess = false;
      state.groupPermsMsg = payload;
    },
    RESET_GET_GROUP_PERMS(state) {
      state.permsLoading = false;
      state.groupPermsSuccess = false;
      state.groupPermsMsg = null;
    },
    ADD_GROUP_PERMS_SUCCESS(state, payload) {
      state.saving = false;
      state.addGroupPermsSuccess = true;
      state.addGroupPermsMsg = payload;
    },
    ADD_GROUP_PERMS_ERROR(state, payload) {
      state.saving = false;
      state.addGroupPermsSuccess = false;
      state.addGroupPermsMsg = payload;
    },
    RESET_ADD_GROUP_PERMS(state) {
      state.saving = false;
      state.addGroupPermsSuccess = false;
      state.addGroupPermsMsg = null;
    },
    REMOVE_GROUP_PERMS_SUCCESS(state, payload) {
      state.saving = false;
      state.removeGroupPermsSuccess = true;
      state.removeGroupPermsMsg = payload;
    },
    REMOVE_GROUP_PERMS_ERROR(state, payload) {
      state.saving = false;
      state.removeGroupPermsSuccess = false;
      state.removeGroupPermsMsg = payload;
    },
    RESET_REMOVE_GROUP_PERMS(state) {
      state.saving = false;
      state.removeGroupPermsSuccess = false;
      state.removeGroupPermsMsg = null;
    },
    RESET_GET_SUBMODULES_PERMS(state) {
      state.getSubModulesPermsLoading = false;
      state.getSubModulesPermsSuccess = false;
      state.getSubModulesPermsData = null;
    },
    GET_SUBMODULES_PERMS_ERROR(state) {
      state.getSubModulesPermsLoading = false;
      state.getSubModulesPermsSuccess = false;
      state.getSubModulesPermsData = null;
    },
    GET_SUBMODULES_PERMS_SUCCESS(state, payload) {
      state.getSubModulesPermsLoading = false;
      state.getSubModulesPermsSuccess = true;
      state.getSubModulesPermsData = payload?.data;
    },
    RESET_GET_SUBMODULES(state) {
      state.subModulesLoading = false;
      state.subModulesSuccess = false;
      state.subModulesData = [];
    },
    GET_SUBMODULES_ERROR(state, payload) {
      state.subModulesLoading = false;
      state.subModulesSuccess = false;
      state.subModulesMsg = payload;
      state.subModulesData = [];
    },
    GET_SUBMODULES_SUCCESS(state, payload) {
      state.subModulesLoading = false;
      state.subModulesSuccess = true;
      state.subModulesData = payload?.data;
      state.subModulesMsg = payload?.Description;
    },
    STAFF_STATUS_UPDATE_SUCCESS(state, payload) {
      state.staffStatusUpdateLoading = false;
      state.staffStatusUpdateSuccess = true;
      state.staffStatusUpdateMsg = payload?.message;
      state.staffStatusValue = payload?.result?.data?.data?.accountStatus;
    },
    STAFF_STATUS_UPDATE_ERROR(state, payload) {
      state.staffStatusUpdateLoading = false;
      state.staffStatusUpdateSuccess = false;
      state.staffStatusUpdateMsg = payload;
    },
    RESET_STAFF_STATUS_UPDATE(state) {
      state.staffStatusUpdateLoading = false;
      state.staffStatusUpdateSuccess = false;
      state.staffStatusUpdateMsg = null;
    },
    EDIT_USER_SUCCESS(state, payload) {
      state.saving = false;
      state.editUserSuccess = true;
      state.editUserMsg = payload;
    },
    EDIT_USER_ERROR(state, payload) {
      state.saving = false;
      state.editUserSuccess = false;
      state.editUserMsg = payload;
    },
    RESET_EDIT_USER(state) {
      state.saving = false;
      state.editUserSuccess = false;
      state.editUserMsg = null;
    },
    ADD_USER_TO_GROUP_SUCCESS(state, payload) {
      state.saving = false;
      state.addUserToGroupSuccess = true;
      state.addUserToGroupMsg = payload;
    },
    ADD_USER_TO_GROUP_ERROR(state, payload) {
      state.saving = false;
      state.addUserToGroupSuccess = false;
      state.addUserToGroupMsg = payload;
    },
    RESET_ADD_USER_TO_GROUP(state) {
      state.saving = false;
      state.addUserToGroupSuccess = false;
      state.addUserToGroupMsg = null;
    },
    REMOVE_USER_FROM_GROUP_LOADING(state, payload: boolean) {
      state.removeUserFromGroupLoading = payload;
    },
    REMOVE_USER_FROM_GROUP_SUCCESS(state, payload) {
      state.removeUserFromGroupLoading = false;
      state.removeUserFromGroupSuccess = true;
      state.removeUserFromGroupMsg = payload;
    },
    REMOVE_USER_FROM_GROUP_ERROR(state, payload) {
      state.removeUserFromGroupLoading = false;
      state.removeUserFromGroupSuccess = false;
      state.removeUserFromGroupMsg = payload;
    },
    RESET_REMOVE_USER_FROM_GROUP(state) {
      state.removeUserFromGroupLoading = false;
      state.removeUserFromGroupSuccess = false;
      state.removeUserFromGroupMsg = null;
    },
    RESET_RESEND_INVITE_LINK(state) {
      state.saving = false;
      state.resendInviteLinkSuccess = false;
      state.resendInviteLinkMsg = null;
    },
    RESEND_INVITE_LINK_SUCCESS(state, payload) {
      state.saving = false;
      state.resendInviteLinkSuccess = true;
      state.resendInviteLinkMsg = payload;
    },
    RESEND_INVITE_LINK_ERROR(state, payload) {
      state.saving = false;
      state.resendInviteLinkSuccess = false;
      state.resendInviteLinkMsg = payload;
    },
    RESET_BRANCHES(state) {
      state.branchListLoading = false;
      state.branchList = [];
      state.branchListSuccess = false;
      state.branchListMsg = '';
      state.branchTotalCount = 0;
    },
    BRANCHES_LOADING(state, payload: boolean) {
      state.branchListLoading = payload;
    },
    SET_BRANCHES(state, { success, message, result }) {
      state.branchListLoading = false;
      state.branchList = result?.data?.data ?? [];
      state.branchListSuccess = success;
      state.branchListMsg = result?.data?.Description ?? message ?? '';
    },
    GET_BRANCHES_SUCCESS(state, payload) {
      state.branchListLoading = false;
      state.branchList = payload?.data?.data ?? [];
      state.branchTotalCount = payload?.data?.totalCount ?? payload?.data?.data?.length ?? 0;
      state.branchListSuccess = true;
      state.branchListMsg = payload?.data?.Description;
    },
    GET_BRANCHES_ERROR(state, payload) {
      state.branchListLoading = false;
      state.branchListSuccess = false;
      state.branchListMsg = payload;
      state.branchList = [];
    },
    CREATE_BRANCH_LOADING(state, payload: boolean) {
      state.createBranchLoading = payload;
    },
    CREATE_BRANCH_SUCCESS(state, payload) {
      state.createBranchLoading = false;
      state.createBranchSuccess = true;
      state.createBranchMsg = payload;
    },
    CREATE_BRANCH_ERROR(state, payload) {
      state.createBranchLoading = false;
      state.createBranchSuccess = false;
      state.createBranchMsg = payload;
    },
    RESET_CREATE_BRANCH(state) {
      state.createBranchLoading = false;
      state.createBranchSuccess = false;
      state.createBranchMsg = null;
      state.createBranch = null;
    },
    SET_CREATE_BRANCH(state, payload) {
      state.createBranchLoading = false;
      state.createBranchSuccess = payload?.success;
      state.createBranchMsg = payload?.result?.data?.Description ?? payload?.message;
      state.createBranch = payload;
    },
    RESET_UPDATE_BRANCH(state) {
      state.updateBranchLoading = false;
      state.updateBranchSuccess = false;
      state.updateBranchMsg = null;
    },
    UPDATE_BRANCH_LOADING(state, payload: boolean) {
      state.updateBranchLoading = payload;
    },
    UPDATE_BRANCH_SUCCESS(state, payload: string) {
      state.updateBranchLoading = false;
      state.updateBranchSuccess = true;
      state.updateBranchMsg = payload;
    },
    UPDATE_BRANCH_ERROR(state, payload: string) {
      state.updateBranchLoading = false;
      state.updateBranchSuccess = false;
      state.updateBranchMsg = payload;
    },
    RESET_BRANCH_STATUS(state) {
      state.branchStatusLoading = false;
      state.branchStatusSuccess = false;
      state.branchStatusMsg = null;
    },
    BRANCH_STATUS_LOADING(state, payload: boolean) {
      state.branchStatusLoading = payload;
    },
    BRANCH_STATUS_SUCCESS(state, payload: string) {
      state.branchStatusLoading = false;
      state.branchStatusSuccess = true;
      state.branchStatusMsg = payload;
    },
    BRANCH_STATUS_ERROR(state, payload: string) {
      state.branchStatusLoading = false;
      state.branchStatusSuccess = false;
      state.branchStatusMsg = payload;
    },
    RESET_REMOVE_MEMBER(state) {
      state.removeMemberLoading = false;
      state.removeMemberSuccess = false;
      state.removeMemberMsg = null;
    },
    REMOVE_MEMBER_LOADING(state, payload: boolean) {
      state.removeMemberLoading = payload;
    },
    REMOVE_MEMBER_SUCCESS(state, payload: string) {
      state.removeMemberLoading = false;
      state.removeMemberSuccess = true;
      state.removeMemberMsg = payload;
    },
    REMOVE_MEMBER_ERROR(state, payload: string) {
      state.removeMemberLoading = false;
      state.removeMemberSuccess = false;
      state.removeMemberMsg = payload;
    },
    RESET_ADD_MODULE(state) {
      state.addModulesLoading = false;
      state.addModulesSuccess = false;
      state.addModulesMsg = null;
    },
    ADD_MODULE_LOADING(state, payload: boolean) {
      state.addModulesLoading = payload;
    },
    ADD_MODULE_SUCCESS(state, payload: string) {
      state.addModulesLoading = false;
      state.addModulesSuccess = true;
      state.addModulesMsg = payload;
    },
    ADD_MODULE_ERROR(state, payload: string) {
      state.addModulesLoading = false;
      state.addModulesSuccess = false;
      state.addModulesMsg = payload;
    },
  },
  actions: {
    async GET_STAFFS(
      { commit, rootState }: ActionContext<IIdentityState, IRootState>,
      params: {
        pageNumber: number;
        pageSize: number;
        accountStatus?: StaffStatusQueryKey;
        isVerification?: boolean;
        getExpiredLicense?: boolean;
        orderByQueryString?: string;
        SearchString?: string;
        isLoadMore?: boolean;
      }
    ) {
      try {
        const isLoadMore = params.isLoadMore ?? false;
        
        if (isLoadMore) {
          commit('SET_LOADING_MORE', true);
        } else {
          commit("LOADING");
        }

        const response = await getStaffs(
          {
            pageNumber: params?.pageNumber ?? 1,
            pageSize: params?.pageSize ?? 10,
            accountStatus: params?.accountStatus ?? StaffStatusQueryKey.ALL,
            isVerification: params?.isVerification ?? false,
            getExpiredLicense: params?.getExpiredLicense ?? false,
            orderByQueryString: params?.orderByQueryString ?? '',
            searchQuery: params?.SearchString ?? '',
            tenantId: rootState.auth.MCAdminCognitoID,
          }
        );

        if (response?.success) {
          commit("GET_STAFFS_SUCCESS", response?.result);
          if (isLoadMore) {
            commit('APPEND_STAFFS_DATA', response?.result?.data?.items || []);
          } else {
            commit('SAVE_STAFFS_DATA', response?.result?.data?.items || []);
          }
        } else {
          commit("GET_STAFFS_ERROR", response?.result?.data?.Description ?? response?.message);
        }
      } catch (error) {
        console.error('Error fetching staffs:', error);
        commit("GET_STAFFS_ERROR", 'Error retrieving practitioners.');
      } finally {
        commit("LOADING_STOP");
        commit('SET_LOADING_MORE', false);
      }
    },
    async CREATE_USER({ commit, rootState }, payload) {
      commit("RESET_SAVE_USER");
      commit("CREATE_USER");
      let response = await addStaff(rootState.auth.MCAdminCognitoID, payload);

      if (response?.success) {
        commit("SAVE_USER_SUCCESS", response?.result?.data?.Description);
      } else {
        commit("SAVE_USER_ERROR", response?.result?.data?.Description);
      }

      commit("CREATE_USER_STOP");
    },
    async CREATE_GROUP({ state, commit, rootState }, payload) {
      commit("RESET_CREATE_GROUP");
      commit("SAVING");
      let response = await createGroup(payload, rootState.auth.MCAdminCognitoID);
      if (response?.success) {
        commit("CREATE_GROUP_SUCCESS", response?.result?.data);
      } else {
        commit("CREATE_GROUP_ERROR", response?.result?.data?.Description);
      }

      commit("SAVING_STOP");
    },
    async GET_GROUPS({ state, commit, rootState }, payload) {
      commit("RESET_GET_GROUPS");
      commit("GROUPS_LOADING");
      let response = await getGroups({
        pageNumber: payload?.pageNumber ?? 1,
        pageSize: payload?.pageSize ?? 10,
        searchQuery: payload?.searchQuery ?? '',
        tenantId: rootState.auth.MCAdminCognitoID,
      });

      if (response?.success) {
        commit("GET_GROUPS_SUCCESS", response?.result?.data);
      } else {
        commit("GET_GROUPS_ERROR", response?.result?.data?.Description);
      }

      commit("GROUPS_LOADING_STOP");
    },
    async GET_GROUP({ state, commit, rootState }, payload) {
      commit("RESET_GET_GROUP");
      commit("SET_GROUP_LOADING", true);

      try {
        const response = await getGroupById({ groupId: payload, tenantId: rootState.auth.MCAdminCognitoID });
        if (response?.success) {
          const details: IGroup = response?.result?.data?.data;
          commit("SET_GROUP_DETAILS", details);
        } else {
          commit("SET_GROUP_DETAILS_ERROR", response?.result?.data?.Description ?? response?.message ?? 'Error fetching group details.');
        }
      } catch {
        commit("SET_GROUP_DETAILS_ERROR", 'Error fetching group details.');
      } finally {
        commit("SET_GROUP_LOADING", false);
      }
    },
    async DELETE_GROUP({ state, commit, rootState }, payload) {
      commit("RESET_DELETE_GROUP");
      commit("SAVING");
      let response = await deleteGroup(payload, rootState.auth.MCAdminCognitoID);

      if (response?.success) {
        commit("DELETE_GROUP_SUCCESS", response?.result?.data?.Description);
      } else {
        commit("DELETE_GROUP_ERROR", response?.result?.data?.Description);
      }

      commit("SAVING_STOP");
    },
    async EDIT_GROUP_NAME({ state, commit, rootState }, payload) {
      commit("RESET_EDIT_GROUP_NAME");
      commit("SAVING");

      let response = await editGroupName(
        payload[0],
        payload[1].groupID,
        rootState.auth.MCAdminCognitoID
      );

      if (response?.success) {
        commit("EDIT_GROUP_NAME_SUCCESS", response?.result?.data.Description);
      } else {
        commit("EDIT_GROUP_NAME_ERROR", response?.result?.data.Description);
      }
      commit("SAVING_STOP");
    },
    async GET_GROUP_PERMS({ state, commit, rootState }, payload) {
      commit("RESET_GET_GROUP_PERMS");
      commit("PERMS_LOADING");

      let response = await getGroupPerms(
        payload.pageNumber,
        payload.pageSize,
        payload.groupID,
        rootState.auth.MCAdminCognitoID
      );

      if (response?.success) {
        commit("GET_GROUP_PERMS_SUCCESS", response?.result?.data);
      } else {
        commit("GET_GROUP_PERMS_ERROR", response?.result?.data?.Description);
      }

      commit("PERMS_LOADING_STOP");
    },
    async ADD_GROUP_PERMS({ state, commit, rootState }, payload) {
      commit("RESET_ADD_GROUP_PERMS");
      commit("SAVING");
      let response = await addGroupPerms(
        payload,
        rootState.auth.MCAdminCognitoID
      );

      if (response?.success) {
        commit("ADD_GROUP_PERMS_SUCCESS", response?.result?.data?.Description);
      } else {
        commit("ADD_GROUP_PERMS_ERROR", response?.result?.data?.Description);
      }

      commit("SAVING_STOP");
    },
    async REMOVE_GROUP_PERMS({ state, commit, rootState }, payload) {
      commit("RESET_REMOVE_GROUP_PERMS");
      commit("SAVING");
      let response = await removeGroupPerms(
        payload,
        rootState.auth.MCAdminCognitoID
      );

      if (response?.success) {
        commit("REMOVE_GROUP_PERMS_SUCCESS", response?.result?.data?.Description);
      } else {
        commit("REMOVE_GROUP_PERMS_ERROR", response?.result?.data?.Description);
      }

      commit("SAVING_STOP");
    },
    async GET_SUBMODULES_PERMS({ state, commit, rootState }, payload) {
      commit("RESET_GET_SUBMODULES_PERMS");
      commit("SUBMODULES_PERMS_LOADING");

      let response = await getSubModulePerms({
        pageNumber: payload?.pageNumber ?? 1,
        pageSize: payload?.pageSize ?? 10,
        submoduleID: payload?.submoduleID ?? 0,
        tenantId: rootState.auth.MCAdminCognitoID,
      });

      if (response?.success) {
        commit("GET_SUBMODULES_PERMS_SUCCESS", response?.result?.data);
      } else {
        commit("GET_SUBMODULES_PERMS_ERROR", response?.result?.data?.Description);
      }
    },
    async GET_SUBMODULES({ state, commit, rootState }, payload) {
      commit("RESET_GET_SUBMODULES");
      commit("SUBMODULES_LOADING");

      let response = await getSubModules({
        pageNumber: payload?.pageNumber ?? 1,
        pageSize: payload?.pageSize ?? 10,
        tenantId: rootState.auth.MCAdminCognitoID,
      });

      if (response?.success) {
        commit("GET_SUBMODULES_SUCCESS", response?.result?.data);
      } else {
        commit("GET_SUBMODULES_ERROR", response?.result?.data?.Description);
      }
    },
    async STAFF_STATUS_UPDATE({ state, commit, rootState }, payload) {
      commit("RESET_STAFF_STATUS_UPDATE");
      commit("STAFF_STATUS_LOADING");
      let response = await staffStatusUpdate(
        payload,
        rootState.auth.MCAdminCognitoID
      );

      if (response?.success) {
        commit("STAFF_STATUS_UPDATE_SUCCESS", response);
      } else {
        commit("STAFF_STATUS_UPDATE_ERROR", response?.result?.items?.Description);
      }

      commit("STAFF_STATUS_LOADING_STOP");
    },
    async EDIT_USER({ commit, rootState }, payload) {
      commit("RESET_EDIT_USER");
      commit("SAVING");
      let response = await editUser(payload, rootState.auth.MCAdminCognitoID);

      if (response?.success) {
        commit("EDIT_USER_SUCCESS", response?.message);
      } else {
        commit("EDIT_USER_ERROR", response?.message);
      }
      commit("SAVING_STOP");
    },
    async ADD_USER_TO_GROUP({ commit, rootState }, payload) {
      commit("RESET_ADD_USER_TO_GROUP");
      commit("SAVING");
      let response = await addUserToGroup(
        payload,
        rootState.auth.MCAdminCognitoID
      );

      if (response?.success) {
        commit("ADD_USER_TO_GROUP_SUCCESS", response?.message);
      } else {
        commit("ADD_USER_TO_GROUP_ERROR", response?.result?.data?.Description);
      }
      commit("SAVING_STOP");
    },
    async REMOVE_USER_FROM_GROUP({ commit, rootState }, payload) {
      commit("RESET_REMOVE_USER_FROM_GROUP");
      commit("REMOVE_USER_FROM_GROUP_LOADING", true);
      try {
        const response = await removeUserFromGroup(payload, rootState.auth.MCAdminCognitoID);
        if (response?.success) {
          commit("REMOVE_USER_FROM_GROUP_SUCCESS", response?.result?.data?.Description ?? 'Practitioner removed from group successfully.');
        } else {
          commit("REMOVE_USER_FROM_GROUP_ERROR", response?.result?.data?.Description ?? response?.message ?? 'Failed to remove practitioner from group.');
        }
      } catch {
        commit("REMOVE_USER_FROM_GROUP_ERROR", 'Failed to remove practitioner from group.');
      } finally {
        commit("REMOVE_USER_FROM_GROUP_LOADING", false);
      }
    },
    async RESEND_INVITE_LINK({ commit, rootState }, payload) {
      commit("RESET_RESEND_INVITE_LINK");
      commit("RESEND_INVITE");
      let response = await resendInviteLink(
        payload,
        rootState.auth.MCAdminCognitoID
      );
      if (response?.success) {
        commit("RESEND_INVITE_LINK_SUCCESS", response?.result?.data?.Description);
      } else {
        commit("RESEND_INVITE_LINK_ERROR", response?.result?.data?.Description);
      }
      commit("RESEND_INVITE_STOP");
    },

    async GET_BRANCHES(
      { commit, rootState },
      params: {
        pageNumber?: number;
        pageSize?: number;
        BranchName?: string;
      } = {}
    ) {
      try {
        commit("RESET_BRANCHES");
        commit("BRANCHES_LOADING", true);

        const response = await getBranches({
          tenantId: rootState.auth.MCAdminCognitoID,
          pageNumber: params.pageNumber ?? 1,
          pageSize: params.pageSize ?? 10,
          branchName: params.BranchName ?? "",
        });

        if (response?.success) {
          commit("GET_BRANCHES_SUCCESS", response?.result);
        } else {
          commit("GET_BRANCHES_ERROR", response?.result?.data?.Description ?? response?.message);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
        commit("GET_BRANCHES_ERROR", 'Error retrieving branches.');
      } finally {
        commit("BRANCHES_LOADING", false);
      }
    },

    async CREATE_BRANCH(
      { commit, dispatch, rootState }: ActionContext<IIdentityState, IRootState>,
      payload: ICreateBranchDetailsPayload & { moduleIds?: number[] }
    ) {
      const { moduleIds, ...branchPayload } = payload;

      commit("RESET_CREATE_BRANCH");
      commit("CREATE_BRANCH_LOADING", true);

      try {
        const tenantId = rootState.auth.MCAdminCognitoID;
        const response = await createBranch(branchPayload, tenantId);

        if (!response?.success) {
          const errorMsg =
            response?.result?.data?.Description ??
            response?.message ??
            'Failed to create branch.';
          commit("CREATE_BRANCH_ERROR", errorMsg);
          return;
        }

        const branchId: number | undefined =
          response?.result?.data?.branchId ??
          response?.result?.data?.data?.branchId ??
          response?.result?.data?.data?.id;

        const successMsg: string =
          response?.result?.data?.Description ??
          response?.message ??
          'Branch created successfully.';

        commit("CREATE_BRANCH_SUCCESS", successMsg);

        if (moduleIds?.length && branchId) {
          dispatch("ADD_MODULES_TO_BRANCH", { branchId, moduleIds });
        }
      } catch {
        commit("CREATE_BRANCH_ERROR", 'An unexpected error occurred. Please try again.');
      } finally {
        commit("CREATE_BRANCH_LOADING", false);
      }
    },

    async UPDATE_BRANCH(
      { commit, rootState }: ActionContext<IIdentityState, IRootState>,
      payload: { branchId: number; data: IUpdateBranchDetailsPayload }
    ) {
      commit("RESET_UPDATE_BRANCH");
      commit("UPDATE_BRANCH_LOADING", true);
      try {
        const response = await updateBranch(payload.branchId, payload.data, rootState.auth.MCAdminCognitoID);
        if (response?.success) {
          commit("UPDATE_BRANCH_SUCCESS", response?.result?.data?.Description ?? 'Branch updated successfully.');
        } else {
          commit("UPDATE_BRANCH_ERROR", response?.result?.data?.Description ?? response?.message ?? 'Failed to update branch.');
        }
      } catch {
        commit("UPDATE_BRANCH_ERROR", 'Failed to update branch.');
      } finally {
        commit("UPDATE_BRANCH_LOADING", false);
      }
    },

    async DEACTIVATE_BRANCH(
      { commit, rootState }: ActionContext<IIdentityState, IRootState>,
      payload: IUpdateBranchStatusPayload
    ) {
      commit("RESET_BRANCH_STATUS");
      commit("BRANCH_STATUS_LOADING", true);
      try {
        const response = await deactivateBranch(payload, rootState.auth.MCAdminCognitoID);
        if (response?.success) {
          commit("BRANCH_STATUS_SUCCESS", response?.result?.data?.Description ?? 'Branch deactivated successfully.');
        } else {
          commit("BRANCH_STATUS_ERROR", response?.result?.data?.Description ?? response?.message ?? 'Failed to deactivate branch.');
        }
      } catch {
        commit("BRANCH_STATUS_ERROR", 'Failed to deactivate branch.');
      } finally {
        commit("BRANCH_STATUS_LOADING", false);
      }
    },

    async REACTIVATE_BRANCH(
      { commit, rootState }: ActionContext<IIdentityState, IRootState>,
      payload: IUpdateBranchStatusPayload
    ) {
      commit("RESET_BRANCH_STATUS");
      commit("BRANCH_STATUS_LOADING", true);
      try {
        const response = await reactivateBranch(payload, rootState.auth.MCAdminCognitoID);
        if (response?.success) {
          commit("BRANCH_STATUS_SUCCESS", response?.result?.data?.Description ?? 'Branch activated successfully.');
        } else {
          commit("BRANCH_STATUS_ERROR", response?.result?.data?.Description ?? response?.message ?? 'Failed to activate branch.');
        }
      } catch {
        commit("BRANCH_STATUS_ERROR", 'Failed to activate branch.');
      } finally {
        commit("BRANCH_STATUS_LOADING", false);
      }
    },

    async SHUTDOWN_BRANCH(
      { commit, rootState }: ActionContext<IIdentityState, IRootState>,
      payload: IUpdateBranchStatusPayload
    ) {
      commit("RESET_BRANCH_STATUS");
      commit("BRANCH_STATUS_LOADING", true);
      try {
        const response = await shutdownBranch(payload, rootState.auth.MCAdminCognitoID);
        if (response?.success) {
          commit("BRANCH_STATUS_SUCCESS", response?.result?.data?.Description ?? 'Branch shut down successfully.');
        } else {
          commit("BRANCH_STATUS_ERROR", response?.result?.data?.Description ?? response?.message ?? 'Failed to shut down branch.');
        }
      } catch {
        commit("BRANCH_STATUS_ERROR", 'Failed to shut down branch.');
      } finally {
        commit("BRANCH_STATUS_LOADING", false);
      }
    },

    async REMOVE_BRANCH_MEMBER(
      { commit, rootState }: ActionContext<IIdentityState, IRootState>,
      payload: { branchId: number; practitionerId: string }
    ) {
      commit("RESET_REMOVE_MEMBER");
      commit("REMOVE_MEMBER_LOADING", true);
      try {
        const response = await removeMemberFromBranch(payload.branchId, payload.practitionerId, rootState.auth.MCAdminCognitoID);
        if (response?.success) {
          commit("REMOVE_MEMBER_SUCCESS", response?.result?.data?.Description ?? 'Member removed successfully.');
        } else {
          commit("REMOVE_MEMBER_ERROR", response?.result?.data?.Description ?? response?.message ?? 'Failed to remove member.');
        }
      } catch {
        commit("REMOVE_MEMBER_ERROR", 'Failed to remove member.');
      } finally {
        commit("REMOVE_MEMBER_LOADING", false);
      }
    },

    async ADD_MODULES_TO_BRANCH(
      { commit, rootState }: ActionContext<IIdentityState, IRootState>,
      payload: { branchId: number; moduleIds: number[] }
    ) {
      commit("RESET_ADD_MODULE");
      commit("ADD_MODULE_LOADING", true);
      try {
        const response = await addModulesToBranch(
          { branchId: payload.branchId, moduleId: payload.moduleIds },
          rootState.auth.MCAdminCognitoID
        );
        if (response?.success) {
          commit("ADD_MODULE_SUCCESS", response?.result?.data?.Description ?? 'Modules assigned successfully.');
        } else {
          commit("ADD_MODULE_ERROR", response?.result?.data?.Description ?? response?.message ?? 'Failed to assign modules to branch.');
        }
      } catch {
        commit("ADD_MODULE_ERROR", 'Failed to assign modules to branch.');
      } finally {
        commit("ADD_MODULE_LOADING", false);
      }
    },
  }
}

export default identity;